import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal.tsx';

/**
 * File a deck or library item under organisation categories. A category is a
 * 1–2 segment path joined by "/" (e.g. "Clients/Acme").
 *
 * Two modes, because the two things being filed have different rules:
 * - default (Library items, bank items): ONE category. A text field with a
 *   datalist so authors reuse an existing client rather than coining a
 *   near-duplicate; blank → Uncategorised. `onSave(path | null)`.
 * - `multi` (decks, since 2026-08-02): ANY NUMBER. A deck stopped having one
 *   "home" when the lifecycle statuses were folded into ordinary categories —
 *   a deck can be a client's AND a test. Existing categories are a checkbox
 *   list (typing a path is the exception, not the interaction), and the whole
 *   set is sent every time. `onSave(string[])`.
 */
type MoveToCategoryModalProps = {
  /** The entity being filed. `categories` drives multi mode, `category` the
   *  single-path one. */
  deck: { title?: string; category?: string | null; categories?: string[] };
  allCategories?: string[];
  busy?: boolean;
  /** Decks file under ANY NUMBER of categories (2026-08-02); library and bank
   *  items still have exactly one. */
  multi?: boolean;
  onCancel: () => void;
  onSave: (next: string[] | string | null) => void;
  moveLabel?: string;
  clearLabel?: string;
};

export default function MoveToCategoryModal({
  deck,
  allCategories = [],
  busy = false,
  multi = false,
  onCancel,
  onSave,
  // Wording defaults to the deck flow; the Library passes item-oriented labels.
  moveLabel = 'Move deck',
  clearLabel = 'Set Uncategorised',
}: MoveToCategoryModalProps) {
  const [value, setValue] = useState(multi ? '' : deck.category || '');
  // Selected paths in multi mode, in their canonical casing.
  const [picked, setPicked] = useState<string[]>(() => (multi ? [...(deck.categories || [])] : []));
  const inputRef = useRef<HTMLInputElement>(null);

  // Modal's focus trap puts focus on the first focusable in the dialog; this
  // dialog wants the caret in the field, with the existing path selected so
  // typing replaces it.
  useEffect(() => {
    inputRef.current?.focus();
    if (!multi) inputRef.current?.select();
  }, [multi]);

  // Normalise to the server's contract: 1–2 non-empty segments, each ≤30 chars.
  // Empty → null (Uncategorised). Returns undefined when the input is invalid.
  function normalise(raw: string): string | null | undefined {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const segs = trimmed
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!segs.length || segs.length > 2 || segs.some((s) => s.length > 30)) return undefined;
    return segs.join('/');
  }

  const normalised = normalise(value);
  const invalid = normalised === undefined;
  const isPicked = (path: string) => picked.some((p) => p.toLowerCase() === path.toLowerCase());

  // Every category the list should offer: the registry's, plus any the deck
  // already carries that has since been removed from it (so opening the dialog
  // can never silently drop one).
  const options = useMemo(() => {
    const out = [...allCategories];
    const seen = new Set(out.map((p) => p.toLowerCase()));
    for (const p of picked) {
      if (!seen.has(p.toLowerCase())) {
        seen.add(p.toLowerCase());
        out.push(p);
      }
    }
    return out;
  }, [allCategories, picked]);

  function toggle(path: string) {
    setPicked((prev) =>
      prev.some((p) => p.toLowerCase() === path.toLowerCase())
        ? prev.filter((p) => p.toLowerCase() !== path.toLowerCase())
        : [...prev, path],
    );
  }

  // Multi mode: the text field ADDS a category rather than being the answer.
  function addTyped() {
    if (!normalised || invalid) return;
    if (!isPicked(normalised)) setPicked((prev) => [...prev, normalised]);
    setValue('');
  }

  function submit() {
    if (multi) {
      if (invalid) return;
      // A path typed but not yet added is plainly intended — take it rather
      // than silently discarding it on Save.
      const extra = normalised && !isPicked(normalised) ? [normalised] : [];
      onSave([...picked, ...extra]);
      return;
    }
    if (invalid) return;
    onSave(normalised); // null clears, a path moves
  }

  return (
    <Modal
      title={multi ? 'Categories' : 'Move to category'}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={submit} disabled={busy || invalid}>
            {multi ? 'Save' : normalised ? moveLabel : clearLabel}
          </button>
        </>
      }
    >
      <p className="muted small" style={{ marginBottom: '0.6rem' }}>
        {deck.title}
      </p>
      {multi && options.length > 0 && (
        <ul className="cat-picker" role="list">
          {options.map((c) => (
            <li key={c}>
              <label className="cat-picker__row">
                <input type="checkbox" checked={isPicked(c)} onChange={() => toggle(c)} />
                <span>{c.replace('/', ' › ')}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={inputRef}
        type="text"
        className="modal__input"
        list="category-suggestions"
        value={value}
        placeholder={multi ? 'Add another, e.g. Clients/Acme' : 'e.g. Clients/Acme, or leave blank for Uncategorised'}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (multi) addTyped();
            else submit();
          }
        }}
      />
      {!multi && (
        <datalist id="category-suggestions">
          {allCategories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      )}
      <p className="muted small" style={{ marginTop: '0.5rem' }}>
        {invalid
          ? 'Use one or two levels (Parent/Child), each up to 30 characters.'
          : multi
            ? picked.length
              ? 'Press Enter to add a new one. A deck can sit in as many categories as you like.'
              : 'None selected — the deck will be Uncategorised.'
            : 'One or two levels: a parent groups its sub-categories in the sidebar.'}
      </p>
    </Modal>
  );
}
